import React, { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dropdown, Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import './ClientNotesTab.scss';

import { ReactComponent as ArrowIcon } from 'src/assets/images/trainer/clients/nav-arrow-icon.svg';
import { ReactComponent as DashboardViewArrowIcon } from 'src/assets/images/trainer/clients/dashboard-view-arrow.svg';
import { ReactComponent as OverviewOverviewAddNoteIcon } from 'src/assets/images/trainer/clients/overview-overview-add_note.svg';
import { ReactComponent as OverviewOverviewMyNotesIcon } from 'src/assets/images/trainer/clients/overview-overview-my_notes.svg';
import { ReactComponent as SearchIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/search.svg';
import { ReactComponent as PinIcon } from 'src/assets/images/trainer/clients/pin.svg';
import { ReactComponent as DeleteIcon } from 'src/assets/images/trainer/clients/dashboard-delete.svg';

import { ReactComponent as DashboardModalDeleteIcon } from 'src/assets/images/trainer/clients/dashboard-modal-delete.svg';
import { Button } from 'src/components/Button/Button';
import { useDebounce } from 'src/hooks/useDebounce';
import { useGetClientNotes } from 'src/api/queries/useGetClientNotes';
import { useDeleteClientNote, usePinClientNote } from 'src/api/mutations/useClientNotesMutations';

import { AddNoteModal } from './AddNoteModal/AddNoteModal';

type ClientNotesTabProps = object;

export const ClientNotesTab: FC<ClientNotesTabProps> = () => {
  const navigate = useNavigate();
  const { uid } = useParams();

  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortFilter, setSortFilter] = useState('newest');

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data: notes = [], isLoading } = useGetClientNotes(
    uid || '',
    sortFilter,
    debouncedSearchQuery,
  );
  const { mutate: deleteNoteMutate, isPending: isDeleting } = useDeleteClientNote();
  const {
    mutate: pinNoteMutate,
    isPending: isPinning,
    variables: pinningVariables,
  } = usePinClientNote();

  const togglePin = (id: string, currentlyPinned: boolean) => {
    if (!uid) return;
    pinNoteMutate({ clientId: uid, noteId: id, pinned: !currentlyPinned });
  };

  const deleteNote = () => {
    if (!noteToDelete || !uid) return;
    deleteNoteMutate(
      { clientId: uid, noteId: noteToDelete },
      {
        onSuccess: () => {
          setNoteToDelete(null);
        },
      },
    );
  };

  const formatDate = (isoStr: string) => {
    try {
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return isoStr;

      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = months[d.getMonth()];
      const day = d.getDate();
      const year = d.getFullYear();

      return `${month} ${day}, ${year}`;
    } catch {
      return isoStr;
    }
  };

  const formatTime = (isoStr: string) => {
    try {
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return isoStr;

      let hours = d.getHours();
      const minutes = d.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'

      return `${hours}:${minutes} ${ampm}`;
    } catch {
      return isoStr;
    }
  };

  return (
    <div className="client-notes-tab">
      <div className="notes-header-bar">
        <div className="left-actions">
          <button
            className="back-btn"
            onClick={() => navigate(`/onboarding-trainer/dashboard/clients/${uid}/overview`)}
          >
            <ArrowIcon className="back-arrow" />
            My Notes
          </button>

          <div className="search-filter-group">
            <div className="search-input-wrapper">
              <SearchIcon className="search-icon" />
              <input
                type="text"
                placeholder="Search note"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="clear-search-btn"
                  aria-label="Clear search"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>

            <Dropdown
              menu={{
                items: [
                  { key: 'newest', label: 'Newest first' },
                  { key: 'oldest', label: 'Oldest first' },
                  { key: 'a-z', label: 'Name (A-Z)' },
                ],
                onClick: (e) => setSortFilter(e.key),
              }}
              trigger={['click']}
              rootClassName="dark-theme-dropdown"
            >
              <div className="filter-dropdown" style={{ cursor: 'pointer' }}>
                <span>
                  {sortFilter === 'newest'
                    ? 'Newest first'
                    : sortFilter === 'oldest' || sortFilter === 'older'
                      ? 'Oldest first'
                      : 'Name (A-Z)'}
                </span>
                <DashboardViewArrowIcon className="dropdown-arrow" />
              </div>
            </Dropdown>
          </div>
        </div>

        <button className="add-note-btn" onClick={() => setIsAddNoteModalOpen(true)}>
          <OverviewOverviewAddNoteIcon /> <span>Add Note</span>
        </button>
      </div>

      <div className="notes-list-container">
        {isLoading ? (
          <div className="empty-state">
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#fff' }} spin />} />
          </div>
        ) : notes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon-container">
              <OverviewOverviewMyNotesIcon />
            </div>
            <p>{searchQuery ? `No results found for "${searchQuery}"` : 'No notes yet.'}</p>
          </div>
        ) : (
          notes.map((note) => (
            <div className="note-card" key={note.id}>
              <div className="note-info-group">
                <div className="note-date-box">
                  <span className="date-part">{formatDate(note.date).split(',')[0]},</span>
                  <span className="year-part">{formatDate(note.date).split(',')[1]}</span>
                </div>
                <div className="note-body">
                  <h4 className="note-title">{note.title}</h4>
                  <p className="note-content">{note.description}</p>
                </div>
              </div>
              <div className="note-actions">
                <div className="top-actions">
                  <button
                    className={`action-btn pin-btn ${note.pinned ? 'active' : ''}`}
                    onClick={() => togglePin(note.id, !!note.pinned)}
                    disabled={isPinning && pinningVariables?.noteId === note.id}
                  >
                    {isPinning && pinningVariables?.noteId === note.id ? (
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 16, color: '#fff' }} spin />}
                      />
                    ) : (
                      <PinIcon />
                    )}
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => setNoteToDelete(note.id)}
                  >
                    <DeleteIcon />
                  </button>
                </div>
                <div className="note-time">{formatTime(note.date)}</div>
              </div>
            </div>
          ))
        )}
      </div>

      <AddNoteModal open={isAddNoteModalOpen} onClose={() => setIsAddNoteModalOpen(false)} />

      <Modal
        open={!!noteToDelete}
        footer={null}
        onCancel={() => setNoteToDelete(null)}
        centered
        width={400}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="icon-wrapper delete">
            <DashboardModalDeleteIcon />
          </div>
          <h2>Delete Note?</h2>
          <p>Are you sure you want to delete this note?</p>
          <div className="logout-actions">
            <Button
              className="btn-logout"
              onClick={deleteNote}
              disabled={isDeleting}
              isLoading={isDeleting}
            >
              Delete
            </Button>
            <Button
              className="btn-cancel"
              onClick={() => setNoteToDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
